import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./FailsafePaywall.module.scss"

/**
 * FAILSAFE (`/failsafe`) — a self-contained FEATURE-SPECIFIC paywall for Private
 * Photo Vault, built on the one real feature no prior paywall has ever heroed:
 * TRUSTED PIN RECOVERY.
 *
 * The concept: a photo vault is only as safe as your ability to get back into it.
 * The quiet fear every vault owner carries isn't a burglar — it's themselves:
 * "what if I forget my PIN and I'm shut out of everything I love?" Premium answers
 * that with a second, private way in. This screen dramatises it: your memories sit
 * in the keep behind a single everyday lock (your PIN). Premium cuts you a SPARE
 * recovery key and seats it in a sealed second slot tied to your trusted email —
 * so there are always two ways back to your photos, and a forgotten PIN never
 * means starting over.
 *
 * Behavioural spine: RISK REVERSAL + honest, gentle loss-aversion reframed as
 * reassurance (never fear-mongering, never fake urgency). Value frame:
 * ACCESS-CONTINUITY / self-lockout insurance — untouched by the 45 prior concepts,
 * which store / find / share / sync / back-up / hide / capture / renew but never
 * guarantee the way *back in*.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/failsafe.md
 *   - Layout:      a LOCKSMITH'S BENCH under a photo KEEP — memory photos behind a
 *                  lock plate with two "way-in" lamps, and a key-cutting rail below.
 *                  Not a centered card, grid, viewfinder, keypad-grid, door, receipt
 *                  or calendar.
 *   - Interaction: CUT-AND-SEAT-A-SPARE — cut a duplicate recovery key, watch it
 *                  glide into the sealed recovery slot and turn, lighting the second
 *                  way in. A mint-a-duplicate mechanic, not a hold/scrub/dial/lever/
 *                  flip/toss/stitch/rope-haul/wind/press.
 *   - Register:    the exhale of reassurance — worry resolved. Not fear, luxury,
 *                  nostalgia, playful-loud or cosmic wonder.
 *   - Art:         LIGHT warm ivory + brand blue = the SAFE/second-way-in state,
 *                  amber = the at-risk / forgotten flicker; real bundled memory
 *                  photos so it reads as a photo vault in three seconds.
 *   - Value:       ACCESS-CONTINUITY — "never locked out of your own memories."
 */

type Phase = "atrisk" | "cutting" | "seating" | "safe"

// Real bundled memory photos (public/recall/*.jpg — no runtime egress). These are
// the emotional + brand anchor: the thing you must never be shut out of.
const MEMORIES = [
  "sunset-1.jpg",
  "beach-2.jpg",
  "dog-1.jpg",
  "coffee-1.jpg",
  "flower-2.jpg",
  "mountain-3.jpg",
]

const FailsafePaywall = () => {
  const { state } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    return m ? Math.round((1 - y / (m * 12)) * 100) : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [phase, setPhase] = useState<Phase>("atrisk")
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const didAutoPlay = useRef(false)

  const clearTimers = () => {
    timers.current.forEach(clearTimeout)
    timers.current = []
  }

  // Cut the spare, then seat + turn it. Sequenced so a passive viewer sees the
  // whole worry→rescue→reassurance beat without touching anything.
  const cutSpare = useCallback(() => {
    setPhase((p) => {
      if (p !== "atrisk") return p
      clearTimers()
      timers.current.push(setTimeout(() => setPhase("seating"), 520))
      timers.current.push(setTimeout(() => setPhase("safe"), 1240))
      native.logMixpanelEvent("html_failsafe_cut", {})
      return "cutting"
    })
  }, [])

  const reset = () => {
    clearTimers()
    setPhase("atrisk")
    native.logMixpanelEvent("html_failsafe_reset", {})
  }

  // Auto-demonstrate the rescue once, shortly after mount.
  useEffect(() => {
    if (didAutoPlay.current) return
    didAutoPlay.current = true
    const t = setTimeout(() => cutSpare(), 950)
    timers.current.push(t)
    return clearTimers
  }, [cutSpare])

  const safe = phase === "safe"
  const working = phase === "cutting" || phase === "seating"
  const waysIn = safe ? 2 : 1

  const choosePlan = (key: "yearly" | "monthly") => {
    if (key === plan) return
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_failsafe_purchase", { plan })
    if (product) native.subscribe(product)
  }

  const toggleBench = () => {
    if (phase === "atrisk") cutSpare()
    else if (safe) reset()
  }

  const onKeyCut = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      toggleBench()
    }
  }

  return (
    <div className={`${styles.page} ${styles[phase]}`}>
      <div className={styles.warmth} aria-hidden />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden>
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.5 4.5 6v6c0 4.6 3.2 7.9 7.5 9.4 4.3-1.5 7.5-4.8 7.5-9.4V6L12 2.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <rect
                x="8.5"
                y="11"
                width="7"
                height="6"
                rx="1.4"
                fill="currentColor"
              />
              <path
                d="M9.6 11V9.3a2.4 2.4 0 0 1 4.8 0V11"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <h1 className={styles.headline}>
        <span className={styles.headA}>
          Forget your PIN?
          <br />
          You still get back in.
        </span>
        <span className={styles.headB}>
          Two ways in.
          <br />
          Both only yours.
        </span>
      </h1>
      <p className={styles.sub}>
        Your vault opens with a PIN only you know. Premium cuts you a second,
        private way back — so a forgotten passcode never shuts you out of your
        own memories.
      </p>

      {/* ── The keep: your memories behind the lock ──────────────────── */}
      <div className={styles.keep}>
        <div className={styles.keepInner}>
          <div className={styles.photos}>
            {MEMORIES.map((file, i) => (
              <span
                className={styles.photo}
                key={file}
                style={{ ["--i" as any]: i }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/recall/${file}`}
                  alt=""
                  aria-hidden
                />
              </span>
            ))}
            <span className={styles.photoGlow} aria-hidden />
          </div>

          <div className={styles.lockPlate}>
            <span className={styles.keyhole} aria-hidden>
              <svg viewBox="0 0 24 24" width="18" height="18">
                <circle cx="12" cy="9.5" r="4" fill="currentColor" />
                <path
                  d="M10 12.5h4l1.2 6.5h-6.4L10 12.5Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span className={styles.lamps}>
              <span className={`${styles.lamp} ${styles.lampOn}`}>
                <i aria-hidden />
                PIN
              </span>
              <span className={`${styles.lamp} ${safe ? styles.lampOn : ""}`}>
                <i aria-hidden />
                Recovery
              </span>
            </span>
            <span className={styles.waysTag}>
              <strong>{waysIn}</strong> {waysIn === 1 ? "way" : "ways"} in
            </span>
          </div>
        </div>
      </div>

      {/* ── The bench: cut & seat the spare recovery key ─────────────── */}
      <div
        className={styles.bench}
        role="button"
        tabIndex={0}
        aria-label={
          safe ? "Reset the demonstration" : "Cut a spare recovery key"
        }
        onClick={toggleBench}
        onKeyDown={onKeyCut}
      >
        {/* master key — your everyday PIN */}
        <div className={styles.station}>
          <span className={`${styles.key} ${styles.master}`} aria-hidden>
            <svg viewBox="0 0 44 20" width="44" height="20">
              <circle
                cx="8"
                cy="10"
                r="6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
              />
              <path
                d="M14 10h26M34 10v5M39 10v6"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className={styles.stationLabel}>Your PIN</span>
        </div>

        <div className={styles.rail}>
          {/* the spark of cutting */}
          <span className={styles.cutter} aria-hidden>
            <span className={styles.spark} />
          </span>
          {/* the spare that travels across and seats */}
          <span className={styles.spare} aria-hidden>
            <svg viewBox="0 0 44 20" width="40" height="18">
              <circle
                cx="8"
                cy="10"
                r="6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
              />
              <path
                d="M14 10h26M34 10v5M39 10v6"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <span className={styles.railLine} aria-hidden />
        </div>

        {/* sealed recovery slot — tied to a trusted email */}
        <div className={styles.station}>
          <span
            className={`${styles.slot} ${safe ? styles.slotFilled : ""}`}
            aria-hidden
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect
                x="4"
                y="7"
                width="16"
                height="12"
                rx="2.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M4.6 8.2 12 13l7.4-4.8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.stationLabel}>Trusted email</span>
        </div>

        <span className={styles.benchHint}>
          {phase === "atrisk"
            ? "Tap to cut your spare"
            : working
              ? "Cutting your spare…"
              : "Spare key sealed · tap to replay"}
        </span>
      </div>

      {/* ── What the spare actually is (honest, three points) ────────── */}
      <ul className={styles.points}>
        <li>
          <span className={styles.dot} aria-hidden />
          Recover through your own trusted email
        </li>
        <li>
          <span className={styles.dot} aria-hidden />
          Only you can use it — nothing leaves your vault
        </li>
        <li>
          <span className={styles.dot} aria-hidden />
          Set once, works whenever you need it
        </li>
      </ul>

      {/* ── Pricing: yearly featured, monthly secondary ─────────────── */}
      <div className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${plan === "yearly" ? styles.yearOn : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearRadio} aria-hidden />
          <div className={styles.yearMain}>
            <span className={styles.yearName}>
              Yearly
              <span className={styles.recommend}>Best value</span>
            </span>
            <span className={styles.yearSub}>
              {yearlyPrice}/year · ≈ {yearlyPerMonth}/mo
            </span>
          </div>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>
              Save {savingsPct}%<em>vs monthly</em>
            </span>
          )}
        </button>

        <button
          className={`${styles.monthRow} ${plan === "monthly" ? styles.monthOn : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthRadio} aria-hidden />
          <span className={styles.monthName}>Monthly</span>
          <span className={styles.monthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaMain}>
          {plan === "yearly" && hasTrial
            ? `Try ${trialDays} Days Free`
            : "Keep My Way In"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? hasTrial
              ? `then ${yearlyPrice}/year (≈ ${yearlyPerMonth}/mo)`
              : `${yearlyPrice}/year (≈ ${yearlyPerMonth}/mo)`
            : `${monthlyPrice}/mo`}
        </span>
      </button>

      <p className={styles.reassure}>
        {plan === "yearly" && hasTrial ? "No charge today · " : ""}
        Cancel anytime · Auto-renews until canceled · Your photos are always
        yours
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default FailsafePaywall
